import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL, BUILD_ID } from "@shared/env";

const DEFAULT_INTERVAL_MS = 10 * 60 * 1000;

type UseVersionCheckOptions = {
  intervalMs?: number;
};

type UseVersionCheckResult = {
  updateAvailable: boolean;
  refresh: () => void;
};

async function fetchServerBuildId(): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/api/v1/public/build_info`);
    if (!response.ok) return null;

    const body = await response.json();
    const buildId = body?.data?.[0]?.buildId;
    return typeof buildId === "string" ? buildId : null;
  } catch {
    return null;
  }
}

export default function useVersionCheck({
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseVersionCheckOptions = {}): UseVersionCheckResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    if (BUILD_ID === "dev") return undefined;

    const check = async () => {
      const serverBuildId = await fetchServerBuildId();
      if (serverBuildId && serverBuildId !== BUILD_ID) {
        setUpdateAvailable(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    intervalRef.current = setInterval(check, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [intervalMs]);

  return { updateAvailable, refresh };
}
